import { useGSAP } from "@gsap/react";
import {
  createElement,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";

import { animations } from "./animations";
import { gsap, SplitText as SplitTextLib } from "../../lib/gsap";
import { useScrollRigScrollTrigger } from "@/mono/lib/hooks/useScrollRigScrollTrigger";
import fitTextLib from "@/mono/lib/fitText";
import useResize from "@/mono/lib/hooks/useResize";

// FIXME: When switching pages and coming back, the text is already visible
// - Can fix manually with external setAnimateIn
// - Best case is to fix here on unmount, check useGSAP hook

const SplitText = forwardRef(
  (
    {
      tag = "span",
      className = "",
      children,
      animation: _animation = "lineClipped",
      type = "lines",

      splitTextOptions = {
        smartWrap: true,
      },
      onScrollParams = {},
      dangerouslySetInnerHTML = false,
      shouldRevert = false,
      animateOnScroll = false,
      fitText: _fitText = {},

      ...props
    },
    ref
  ) => {
    const localRef = useRef(null);
    const splitRefChild = useRef(null);
    const [elOriginal, elDouble] = [useRef(null), useRef(null)];
    const [splitRefOriginal, splitRefDouble] = [useRef(null), useRef(null)];

    // SSR guard
    const [isMounted, setIsMounted] = useState(false);

    // Flag to check if animated in
    // So when onSplit triggers, we know its resize and we can avoid
    // the text becoming invisible again
    const isAnimatedIn = useRef(false);

    // Setup tracking to prevent race conditions
    // const setupRef = useRef({ id: 0 });

    /**
     * Promises
     */
    const hasSplitPromise = useRef(null);
    const hasSplitPromiseResolve = useRef(null);

    const hasFitTextPromise = useRef(null);
    const hasFitTextPromiseResolve = useRef(null);

    function resetFitTextPromises() {
      // Cancel any pending promise
      if (hasFitTextPromiseResolve.current) {
        hasFitTextPromiseResolve.current();
      }

      hasFitTextPromise.current = new Promise((res) => {
        hasFitTextPromiseResolve.current = res;
      });
    }

    function resetSplitPromises() {
      // Cancel any pending promise
      if (hasSplitPromiseResolve.current) {
        hasSplitPromiseResolve.current();
      }

      hasSplitPromise.current = new Promise((res) => {
        hasSplitPromiseResolve.current = res;
      });
    }

    /**
     * Animation references for kill / interruptions
     */
    const animateInOriginalRef = useRef(null);
    const animateInDoubleRef = useRef(null);
    const animateOutOriginalRef = useRef(null);
    const animateOutDoubleRef = useRef(null);

    const animateInHoverOriginalRef = useRef(null);
    const animateInHoverDoubleRef = useRef(null);
    const animateOutHoverOriginalRef = useRef(null);
    const animateOutHoverDoubleRef = useRef(null);

    const killAllTransitionAnimations = useCallback(() => {
      if (animateInOriginalRef.current) {
        animateInOriginalRef.current.kill();
        animateInOriginalRef.current = null;
      }
      if (animateInDoubleRef.current) {
        animateInDoubleRef.current.kill();
        animateInDoubleRef.current = null;
      }
      if (animateOutOriginalRef.current) {
        animateOutOriginalRef.current.kill();
        animateOutOriginalRef.current = null;
      }
      if (animateOutDoubleRef.current) {
        animateOutDoubleRef.current.kill();
        animateOutDoubleRef.current = null;
      }
    }, []);

    const killAllHoverAnimations = useCallback(() => {
      if (animateInHoverOriginalRef.current) {
        animateInHoverOriginalRef.current.kill();
        animateInHoverOriginalRef.current = null;
      }
      if (animateInHoverDoubleRef.current) {
        animateInHoverDoubleRef.current.kill();
        animateInHoverDoubleRef.current = null;
      }
      if (animateOutHoverOriginalRef.current) {
        animateOutHoverOriginalRef.current.kill();
        animateOutHoverOriginalRef.current = null;
      }
      if (animateOutHoverDoubleRef.current) {
        animateOutHoverDoubleRef.current.kill();
        animateOutHoverDoubleRef.current = null;
      }
    }, []);

    const content = useMemo(() => {
      if (typeof children === "string") return children;
      return "";
    }, [children]);

    const typeKey = useMemo(() => {
      const t = typeof type === "string" ? type : "lines";
      if (t.includes("chars")) return "chars";
      if (t.includes("words")) return "words";
      if (t.includes("lines")) return "lines";
      return "lines";
    }, [type]);

    // Retrieves pre-defined animation from animation object when string
    // otherwise animation can be passed through
    const animation = useMemo(() => {
      return typeof _animation === "string"
        ? animations[_animation]
        : _animation;
    }, [_animation]);

    const [isDouble, isMasked, shouldFitText, shouldDangerouslySetInnerHTML] =
      useMemo(() => {
        const animationName = (animation?.name || "").toLowerCase();

        return [
          animationName.includes("double"),
          animationName.includes("masked") || animationName.includes("clip"),
          _fitText && Object.keys(_fitText).length > 0,
          !!dangerouslySetInnerHTML?.__html,
        ];
      }, [animation, _fitText, dangerouslySetInnerHTML]);

    const fitText = useCallback(() => {
      if (!shouldFitText || !isMounted || !localRef.current) return;

      resetFitTextPromises();

      // Use the element's natural CSS font-size as the upper bound so
      // fitText shrinks to fit but never grows past the designed size.
      const naturalFontSize = Math.round(
        parseFloat(window.getComputedStyle(localRef.current).fontSize) || 0
      );

      fitTextLib(localRef.current, {
        ...(naturalFontSize > 0 && { maxFontSize: naturalFontSize }),
        ..._fitText,
        parentNode: localRef.current?.parentElement,
        parentFitMode: "both",
        afterFit: () => {
          if (hasFitTextPromiseResolve.current) {
            hasFitTextPromiseResolve.current();
          }
        },
      });
    }, [shouldFitText, _fitText, isMounted]);

    const show = () => {
      if (isDouble) {
        if (elOriginal.current) {
          elOriginal.current.style.visibility = "visible";
        }
        if (elDouble.current) {
          elDouble.current.style.visibility = "visible";
        }
      } else {
        if (localRef.current) {
          localRef.current.style.visibility = "visible";
        }
      }
    };

    const hide = () => {
      if (isDouble) {
        if (elOriginal.current) {
          elOriginal.current.style.visibility = "hidden";
        }
        if (elDouble.current) {
          elDouble.current.style.visibility = "hidden";
        }
      } else {
        if (localRef.current) {
          localRef.current.style.visibility = "hidden";
        }
      }
    };

    const setup = useCallback(async () => {
      if (!isMounted) return;

      // const currentSetup = ++setupRef.current.id;

      // Ensure layout-affecting class isn't present during measurement
      if (localRef.current) {
        localRef.current.classList.remove("splittext-ready");
      }

      hide();
      resetSplitPromises();

      await document.fonts.ready;

      // Check if this setup is still current
      // if (setupRef.current.id !== currentSetup) return;

      const handleOnSplit = () => {
        // Check if this setup is still current
        // if (setupRef.current.id !== currentSetup) return;

        if (!isAnimatedIn.current) {
          setAnimateIn();
        }

        if (hasSplitPromiseResolve.current) {
          hasSplitPromiseResolve.current();
        }

        // Mark element as ready so CSS that fixes clipping can apply post-measurement
        if (localRef.current) {
          localRef.current.classList.add("splittext-ready");
        }
      };

      if (shouldFitText) {
        console.log("fitText", children);
        fitText();
        await hasFitTextPromise.current;

        // Check if this setup is still current
        // if (setupRef.current.id !== currentSetup) return;
      }

      if (shouldDangerouslySetInnerHTML) {
        if (isDouble) {
          if (elOriginal.current)
            elOriginal.current.innerHTML = dangerouslySetInnerHTML.__html;
          if (elDouble.current)
            elDouble.current.innerHTML = dangerouslySetInnerHTML.__html;
        } else {
          if (localRef.current)
            localRef.current.innerHTML = dangerouslySetInnerHTML.__html;
        }
      }

      // Check if this setup is still current before creating SplitText
      // if (setupRef.current.id !== currentSetup) return;

      if (isDouble) {
        splitRefOriginal.current = new SplitTextLib(elOriginal.current, {
          type,
          linesClass: `splittext-child`,
          charsClass: `splittext-char`,
          mask: isMasked ? typeKey : undefined,
          onSplit: () => {
            // Check if this setup is still current
            // if (setupRef.current.id !== currentSetup) return;

            splitRefDouble.current = new SplitTextLib(elDouble.current, {
              type,
              linesClass: `splittext-child`,
              charsClass: `splittext-char`,
              mask: isMasked ? typeKey : undefined,
              onSplit: handleOnSplit,
              onInterrupt: () => {},
              autoSplit: true,
            });
          },
          onInterrupt: () => {},
          autoSplit: true,
          ...splitTextOptions,
        });
      } else {
        splitRefChild.current = new SplitTextLib(localRef.current, {
          type,
          linesClass: `splittext-child`,
          charsClass: `splittext-char`,
          mask: isMasked ? typeKey : undefined,
          onSplit: handleOnSplit,
          onInterrupt: () => {},
          autoSplit: true,
          ...splitTextOptions,
        });
      }
    }, [
      isDouble,
      isMasked,
      type,
      typeKey,
      shouldFitText,
      shouldDangerouslySetInnerHTML,
      children,
      isMounted,
      fitText,
    ]);

    const setAnimateIn = useCallback(() => {
      isAnimatedIn.current = false;

      if (isDouble) {
        if (splitRefOriginal.current?.[typeKey]) {
          gsap.set(splitRefOriginal.current[typeKey], animation.set.original);
        }
        if (splitRefDouble.current?.[typeKey]) {
          gsap.set(splitRefDouble.current[typeKey], animation.set.double);
        }
      } else {
        if (splitRefChild.current?.[type]) {
          gsap.set(splitRefChild.current[type], animation.set.original);
        }
      }
    }, [
      type,
      typeKey,
      splitRefOriginal,
      splitRefDouble,
      splitRefChild,
      isDouble,
      animation,
    ]);

    const animateIn = useCallback(
      async (params = {}) => {
        const waitForSplit = async () => {
          let attempts = 0;
          while (!splitRefChild.current?.[type] && attempts < 50) {
            if (hasSplitPromise.current) await hasSplitPromise.current;
            else await new Promise((r) => requestAnimationFrame(r));
            attempts++;
          }
        };
        if (!splitRefChild.current?.[type]) await waitForSplit();
        if (isDouble && (!splitRefOriginal.current?.[typeKey] || !splitRefDouble.current?.[typeKey])) {
          if (hasSplitPromise.current) await hasSplitPromise.current;
        }

        killAllTransitionAnimations();
        setAnimateIn();

        show();

        const handleOnComplete = () => {
          isAnimatedIn.current = true;
          params?.onComplete?.();
        };

        if (isDouble) {
          const { original, double } = animation.in(params);

          animateInOriginalRef.current = gsap.to(
            splitRefOriginal.current?.[typeKey],
            {
              ...original,
              onInterrupt: () => {
                animateInOriginalRef.current.kill();
                animateInOriginalRef.current = null;
              },
              onComplete: () => {
                animateInOriginalRef.current = null;
                handleOnComplete();
              },
            }
          );
          animateInDoubleRef.current = gsap.to(
            splitRefDouble.current?.[typeKey],
            {
              ...double,
              onInterrupt: () => {
                animateInDoubleRef.current.kill();
                animateInDoubleRef.current = null;
              },
              onComplete: () => {
                animateInDoubleRef.current = null;
                handleOnComplete();
              },
            }
          );
        } else {
          const { original } = animation.in(params);

          animateInOriginalRef.current = gsap.to(
            splitRefChild.current?.[type],
            {
              ...original,
              onInterrupt: () => {
                animateInOriginalRef.current.kill();
                animateInOriginalRef.current = null;
              },

              onComplete: () => {
                animateInOriginalRef.current = null;
                handleOnComplete();

                // TODO:
                // if (!shouldRevert || shouldAnimateOut) return;
                // splitRefChild.current?.revert();
              },
            }
          );
        }
      },
      [animation, type, typeKey, isDouble, setAnimateIn]
    );

    const animateOut = useCallback(
      (params = {}) => {
        killAllTransitionAnimations();

        const initialParams = animation.out(params);

        const tp = initialParams.type ?? typeKey;

        if (isDouble) {
          const { double } = initialParams;

          animateOutDoubleRef.current = gsap.to(splitRefDouble.current?.[tp], {
            ...double,
            onInterrupt: () => {
              animateOutDoubleRef.current.kill();
              animateOutDoubleRef.current = null;
            },
            onComplete: () => {
              animateOutDoubleRef.current = null;
              params?.onComplete?.();
              setAnimateIn();
              hide();
            },
          });
        } else {
          const { original } = initialParams;

          animateOutOriginalRef.current = gsap.to(splitRefChild.current?.[tp], {
            ...original,
            onInterrupt: () => {
              animateOutOriginalRef.current.kill();
              animateOutOriginalRef.current = null;
            },
            onComplete: () => {
              animateOutOriginalRef.current = null;
              params?.onComplete?.();
              setAnimateIn();
              hide();
            },
          });
        }
      },
      [
        animation,
        splitRefDouble,
        typeKey,
        setAnimateIn,
        isDouble,
        killAllTransitionAnimations,
      ]
    );

    const hoverIn = useCallback(
      (params = {}) => {
        if (!animation.hoverIn) return;

        killAllHoverAnimations();

        if (isDouble) {
          gsap.set(
            splitRefDouble.current?.[typeKey],
            animation.setHover.double
          );

          const { double } = animation.hoverIn(params);
          animateInHoverDoubleRef.current = gsap.to(
            splitRefDouble.current?.[typeKey],
            {
              ...double,
              ...params,
              onInterrupt: () => {
                animateInHoverDoubleRef.current.kill();
                animateInHoverDoubleRef.current = null;
              },
              onComplete: () => {
                animateInHoverDoubleRef.current = null;
                params?.onComplete?.();
              },
            }
          );
        }

        gsap.set(
          splitRefOriginal.current?.[typeKey],
          animation.setHover.original
        );

        const { original } = animation.hoverIn(params);
        animateInHoverOriginalRef.current = gsap.to(
          splitRefOriginal.current?.[typeKey],
          {
            ...original,
            ...params,
            onInterrupt: () => {
              animateInHoverOriginalRef.current.kill();
              animateInHoverOriginalRef.current = null;
            },
            onComplete: () => {
              animateInHoverOriginalRef.current = null;
              params?.onComplete?.();
            },
          }
        );
      },
      [
        animation,
        splitRefDouble,
        splitRefOriginal,
        typeKey,
        isDouble,
        killAllHoverAnimations,
      ]
    );

    const hoverOut = useCallback(
      (params = {}) => {
        if (!animation.hoverOut) return;

        killAllHoverAnimations();

        if (isDouble) {
          const { double } = animation.hoverOut(params);

          animateOutHoverDoubleRef.current = gsap.to(
            splitRefDouble.current?.[typeKey],
            {
              ...double,
              ...params,
              onInterrupt: () => {
                animateOutHoverDoubleRef.current.kill();
                animateOutHoverDoubleRef.current = null;
              },
              onComplete: () => {
                animateOutHoverDoubleRef.current = null;
                params?.onComplete?.();
              },
            }
          );
        }

        const { original } = animation.hoverOut(params);

        animateOutHoverOriginalRef.current = gsap.to(
          splitRefOriginal.current?.[typeKey],
          {
            ...original,
            ...params,
            onInterrupt: () => {
              animateOutHoverOriginalRef.current.kill();
              animateOutHoverOriginalRef.current = null;
            },
            onComplete: () => {
              animateOutHoverOriginalRef.current = null;
              params?.onComplete?.();
            },
          }
        );
      },
      [
        animation,
        splitRefDouble,
        splitRefOriginal,
        typeKey,
        isDouble,
        killAllHoverAnimations,
      ]
    );

    const isReady = () => hasSplitPromise.current;

    // SSR guard effect
    useEffect(() => {
      setIsMounted(true);
    }, []);

    // Cleanup effect
    useEffect(() => {
      return () => {
        // Cancel any pending operations
        // setupRef.current.id++;

        // Cleanup GSAP animations
        killAllTransitionAnimations();
        killAllHoverAnimations();

        // Revert SplitText instances
        if (splitRefChild.current) {
          splitRefChild.current.revert();
          splitRefChild.current = null;
        }
        if (splitRefOriginal.current) {
          splitRefOriginal.current.revert();
          splitRefOriginal.current = null;
        }
        if (splitRefDouble.current) {
          splitRefDouble.current.revert();
          splitRefDouble.current = null;
        }

        // Remove post-split class on cleanup
        if (localRef.current) {
          localRef.current.classList.remove("splittext-ready");
        }

        // Cancel any pending promises
        if (hasSplitPromiseResolve.current) {
          hasSplitPromiseResolve.current();
        }
        if (hasFitTextPromiseResolve.current) {
          hasFitTextPromiseResolve.current();
        }
      };
    }, [killAllTransitionAnimations, killAllHoverAnimations]);

    useGSAP(setup, {
      scope: localRef,
      dependencies: [
        content,
        shouldFitText,
        isDouble,
        isMasked,
        type,
        typeKey,
        isMounted,
        shouldDangerouslySetInnerHTML,
      ],
      revertOnUpdate: true,
    });

    useImperativeHandle(ref, () => ({
      animateIn,
      animateOut,
      show,
      hide,
      hoverIn,
      hoverOut,
      isReady,
      element: localRef.current,
    }));

    useResize(
      () => {
        if (shouldFitText && isMounted) {
          fitText();
        }
      },
      { immediate: false }
    );

    useScrollRigScrollTrigger({
      el: animateOnScroll && isMounted ? localRef : null,
      offset: 0.6,
      ...onScrollParams,
      handleIn: async () => {
        if (!animateOnScroll || !isMounted) return;

        // TODO: Maybe we shouldnt await this, and rather use a flag
        // when the promises are settled, and just return?
        // On the other hand, it does tie in neatly when being in the viewport
        // await isReady();

        animateIn();
      },
    });

    const createTextContent = (content) => {
      // TODO: Test if null is not rendered?

      const _content = shouldDangerouslySetInnerHTML ? null : content;

      if (isDouble) {
        return (
          <span className="splittext-parent">
            <span
              ref={elOriginal}
              className="splittext-original"
              style={{ visibility: "hidden" }}
            >
              {_content}
            </span>
            <span
              ref={elDouble}
              className="splittext-double"
              style={{ visibility: "hidden" }}
            >
              {_content}
            </span>
          </span>
        );
      }

      return _content;
    };

    // SSR fallback - render simple version without animations
    if (!isMounted) {
      return createElement(
        tag,
        {
          className,
          ...props,
        },
        content
      );
    }

    return createElement(
      tag,
      {
        ref: localRef,
        className,
        style: { visibility: "hidden" },
        ...props,
      },
      createTextContent(content)
    );
  }
);

SplitText.displayName = "CustomSplitText";
export default SplitText;
